import { useContext, useState } from 'react';

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@material-ui/core';
import { FormProvider } from 'react-hook-form';
import { noop } from 'lodash';

import { DeviceContext } from '@vyce/core/src/contexts';
import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import { ButtonTitleWithLoading } from '@vyce/core/src/components/ButtonTitleWithLoading';
import { FormDoc, User } from '@vyce/core/src/types';

import { useFormRecipients } from '../hooks/useFormRecipients';
import { FormSpecificContacts } from './FormSpecificContacts';
import { FormRecipientsSummary } from './FormRecipientsSummary';

interface Props {
  open: boolean;
  onClose: () => void;
  form: FormDoc | null;
  user: User;
  companyId: string;
  /** Called after invites are sent successfully (e.g. to refresh the recipients list). */
  onSent?: () => void;
}

export const FormInviteRecipientsDialog: React.FC<Props> = ({
  open,
  onClose,
  form,
  user,
  companyId,
  onSent,
}) => {
  const { isMobile } = useContext(DeviceContext);
  const [sending, setSending] = useState(false);

  const {
    isPhoneInputDisabled,
    handleEmailFieldKeyDown,
    handlePhoneFieldKeyDown,
    addEmailToList,
    addPhoneToList,
    deleteContact,
    clearContacts,
    emails,
    phones,
    selectedWorkers,
    toggleWorker,
    setIsPhoneInputDisabled,
    bulkPaste,
    bulkLimitHint,
    recipientsFormMethods,
    sendFormRecipients,
  } = useFormRecipients({ user, companyId, form });

  const formId = form?.id;
  const hasContacts = emails.length > 0 || phones.length > 0 || selectedWorkers.length > 0;

  const handleClose = () => {
    clearContacts();
    onClose();
  };

  const handleSend = async () => {
    if (!formId || !hasContacts) {
      return;
    }
    try {
      setSending(true);
      await sendFormRecipients(formId);
      clearContacts();
      onSent?.();
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      open={open}
      onClose={handleClose}>
      <DialogTitle>Invite recipients</DialogTitle>

      <DialogContent style={{ minHeight: 420 }}>
        <FormProvider {...recipientsFormMethods}>
          <form onSubmit={recipientsFormMethods.handleSubmit(noop)}>
            <FormSpecificContacts
              companyId={companyId}
              selectedWorkers={selectedWorkers}
              toggleWorker={toggleWorker}
              isPhoneInputDisabled={isPhoneInputDisabled}
              handleEmailFieldKeyDown={handleEmailFieldKeyDown}
              handlePhoneFieldKeyDown={handlePhoneFieldKeyDown}
              addEmailToList={addEmailToList}
              addPhoneToList={addPhoneToList}
              deleteContact={deleteContact}
              emails={emails}
              phones={phones}
              setIsPhoneInputDisabled={setIsPhoneInputDisabled}
              bulkPaste={bulkPaste}
              bulkLimitHint={bulkLimitHint}
              listMaxHeight="45vh"
            />
          </form>
        </FormProvider>

        {hasContacts && (
          <Box marginTop={2}>
            <FormRecipientsSummary
              selectedWorkers={selectedWorkers}
              onRemoveWorker={toggleWorker}
              emails={emails}
              phones={phones}
              onRemoveContact={deleteContact}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Box display="flex" gridGap={16}>
          <Button size="large" variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            size="large"
            variant="contained"
            color="primary"
            style={{ minWidth: 140 }}
            onClick={handleSend}
            disabled={sending || !hasContacts || !formId}>
            <ButtonTitleWithLoading title="Send invites" loaderVariant="primary" loading={sending} />
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
